/**
 * PERSISTENT STORAGE — and reporting HONESTLY when the browser says no.
 * ====================================================================
 *
 * THE HAZARD, STATED PLAINLY. iOS/iPadOS Safari evicts a site's IndexedDB after a period of no
 * interaction (Apple has documented a seven-day window for script-writable storage; the exact policy
 * has changed more than once and differs for a site installed to the Home Screen). Our users are
 * field technicians who may install this app, do a job on Monday, and not open it again until the
 * following week. **A queued submission can therefore be deleted by the operating system while the
 * technician believes it is safe.**
 *
 * `navigator.storage.persist()` asks for exemption. On Chrome/Android it is usually granted for an
 * installed PWA; on Safari it may be refused outright, and on older WebKit the whole StorageManager
 * API is absent.
 *
 * **THE RULE THIS MODULE EXISTS TO ENFORCE: A REFUSAL MUST NOT BE ROUNDED UP TO A YES.** So
 * {@link assessStorage} returns a four-way verdict and never a boolean:
 *
 *   · `persisted`     — granted. Work is exempt from routine eviction.
 *   · `not-persisted` — **the browser was asked and said NO.** Queued work can be evicted. The UI
 *                       says so, in those words.
 *   · `unsupported`   — no StorageManager. We could not even ask, so we know nothing. Treated as
 *                       "not safe", because the browsers that lack it are the ones that evict.
 *   · `error`         — the call threw. Also treated as "not safe".
 *
 * Note there is no `unknown-but-probably-fine`. This project's recurring defect is a silent failure
 * dressed as a success, and *"storage is probably persistent"* on a technician's phone is that defect
 * with a progress spinner.
 *
 * ⚠️ **WHAT PERSISTENCE DOES NOT BUY, because a half-truth here is worse than nothing.** Even
 * `persisted` does not protect against: the user clearing website data, iOS offloading the app, a
 * quota exceeded error under real memory pressure, or the user deleting the PWA. So persistence is a
 * mitigation and the real fix is draining the queue promptly — which is why `sync.ts` flushes on
 * `visibilitychange` rather than waiting for an `online` event that iOS may never fire.
 *
 * ⚠️ **UNVERIFIED ON REAL DEVICES.** I have not run this on an iPhone or an iPad. The behaviour above
 * is from documentation and from the shape of the API, and the code paths are covered by tests with a
 * faked `navigator.storage`. Treat "persistence granted on iOS" as untested.
 *
 * ─── THE FOUR SENTENCES ARE NOW TRANSLATABLE, AND THE BLUNTNESS IS PART OF THE STRING ──────────
 *
 * The wording lives in `src/i18n/uiStrings.ts` under `ui.storage.*`, with each entry carrying a
 * translator note that says what must not be softened. `"This phone did NOT agree to protect saved
 * work"` is not house style, it is the module's whole argument in one sentence, and a translator who
 * renders it as *"storage may be limited"* reintroduces exactly the defect this file exists to
 * prevent — so the note says so, in those words, next to the string.
 *
 * The four must also stay **mutually distinguishable in every locale**. `not-persisted` (asked,
 * refused) and `unsupported` (could not ask) are facts about different people's problems: one is the
 * technician's to work around today, the other is a browser-support gap to report. A translation that
 * collapses them into one hedge loses that, and `test/i18n/blocking-messages.test.ts` asserts all four
 * differ and that exactly one of them is the reassuring one.
 */

import { englishMessages } from '../i18n/messages.ts';
import type { Messages } from '../i18n/messages.ts';
import { STORAGE_KEYS } from '../i18n/uiStrings.ts';

export type StoragePersistence = 'persisted' | 'not-persisted' | 'unsupported' | 'error';

export interface StorageAssessment {
  readonly persistence: StoragePersistence;
  /** `true` only for `persisted`. The single value the UI should branch on for reassurance. */
  readonly durable: boolean;
  /** Bytes in use, when the browser will say. Approximate by specification. */
  readonly usageBytes: number | null;
  /** The origin's quota, when the browser will say. */
  readonly quotaBytes: number | null;
  /**
   * A sentence for the UI. Written to be read by a technician, not an engineer. Resolved for the
   * active locale; English when no `Messages` was supplied.
   */
  readonly message: string;
  /**
   * The `ui.storage.*` key `message` came from.
   *
   * One key per verdict, and the mapping is total: a banner can re-render in a newly-chosen locale
   * without re-running the assessment, and a test can assert *which* verdict's sentence it is showing
   * rather than pattern-matching English prose — which is how a softened translation would slip past.
   */
  readonly messageKey: string;
  /** Detail for a log line when `persistence` is `error`. */
  readonly detail?: string;
}

interface StorageManagerLike {
  persist?: () => Promise<boolean>;
  persisted?: () => Promise<boolean>;
  estimate?: () => Promise<{ usage?: number; quota?: number }>;
}

function storageManager(): StorageManagerLike | undefined {
  const nav = globalThis.navigator as (Navigator & { storage?: StorageManagerLike }) | undefined;
  return nav?.storage;
}

/**
 * Ask for persistence (if we have not already been granted it) and report what happened.
 *
 * `persisted()` is checked FIRST. `persist()` can show a permission prompt in some browsers, and
 * re-prompting an already-granted origin on every launch would be both pointless and the kind of
 * thing a user learns to dismiss.
 */
export interface AssessStorageOptions {
  /** The active locale's messages. Omit for English. */
  readonly messages?: Messages;
}

export async function assessStorage(options: AssessStorageOptions = {}): Promise<StorageAssessment> {
  const messages = options.messages ?? englishMessages();
  const manager = storageManager();
  const estimate = await safeEstimate(manager);

  /*
   * One verdict, one key, one sentence — built in one place so a fifth outcome cannot be added with a
   * hand-written string, and so `messageKey` can never disagree with `message`.
   */
  const say = (key: string): { message: string; messageKey: string } => ({
    message: messages.m(key),
    messageKey: key,
  });

  if (manager === undefined || typeof manager.persist !== 'function') {
    return {
      persistence: 'unsupported',
      durable: false,
      ...estimate,
      ...say(STORAGE_KEYS.unsupported),
    };
  }

  try {
    const already = typeof manager.persisted === 'function' ? await manager.persisted() : false;
    const granted = already ? true : await manager.persist();
    if (granted) {
      return {
        persistence: 'persisted',
        durable: true,
        ...estimate,
        ...say(STORAGE_KEYS.persisted),
      };
    }
    return {
      persistence: 'not-persisted',
      durable: false,
      ...estimate,
      // The refusal, in the plainest words available. NOT "storage may be limited" — see the header.
      ...say(STORAGE_KEYS.notPersisted),
    };
  } catch (error) {
    return {
      persistence: 'error',
      durable: false,
      ...estimate,
      ...say(STORAGE_KEYS.error),
      detail: error instanceof Error ? error.message : String(error),
    };
  }
}

async function safeEstimate(
  manager: StorageManagerLike | undefined,
): Promise<{ usageBytes: number | null; quotaBytes: number | null }> {
  if (manager === undefined || typeof manager.estimate !== 'function') {
    return { usageBytes: null, quotaBytes: null };
  }
  try {
    const result = await manager.estimate();
    return {
      usageBytes: typeof result.usage === 'number' ? result.usage : null,
      quotaBytes: typeof result.quota === 'number' ? result.quota : null,
    };
  } catch {
    // An estimate failing must not turn a successful persist into an `error` verdict.
    return { usageBytes: null, quotaBytes: null };
  }
}
